import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatButtonModule } from "@angular/material/button";
import { LiveProductionService } from "../../services/live-production.service";
import { LineLiveSnapshot } from "../../models/live-production.model";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { BigButtonComponent } from "../../../../shared/components/big-button/big-button.component";

interface DialogData {
  ligneId: string;
}

/**
 * Drawer plein écran "pilotage machine compact" pour une ligne. Affiche les
 * mesures live (cadence inst/moy, durée sans arrêt, durée totale arrêt,
 * nb arrêts, sparkline) + infos OF actif et conducteurs. Mis à jour live
 * via le `LiveProductionService` (signal réactif).
 */
@Component({
  selector: "vl-live-line-detail-dialog",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    ChipsComponent,
    BigButtonComponent,
  ],
  templateUrl: "./live-line-detail-dialog.component.html",
  styleUrl: "./live-line-detail-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LiveLineDetailDialogComponent {
  service = inject(LiveProductionService);
  ref = inject<MatDialogRef<LiveLineDetailDialogComponent>>(MatDialogRef);

  ligneId: string;

  ligne = computed<LineLiveSnapshot | null>(() => {
    return this.service.lignes().find((l) => l.ligneId === this.ligneId) ?? null;
  });

  cadence = computed(() => this.service.cadenceFor(this.ligneId));

  cadenceInst = computed(() => this.cadence()?.cadenceInstantUH ?? null);
  cadenceMoy = computed(() => this.cadence()?.cadenceMoyenneUH ?? null);

  cadenceVsCible = computed<"high" | "mid" | "low" | "neutral">(() => {
    const v = this.cadenceInst();
    const cible = this.ligne()?.cadenceCibleUH;
    if (v == null || !cible) return "neutral";
    const ratio = v / cible;
    if (ratio >= 0.9) return "high";
    if (ratio >= 0.6) return "mid";
    return "low";
  });

  cadenceChipState = computed(() => {
    switch (this.cadenceVsCible()) {
      case "high": return "valid";
      case "mid": return "warn";
      case "low": return "invalid";
      default: return "basic";
    }
  });

  progressPct = computed(() => {
    const o = this.ligne()?.currentOf;
    if (!o || !o.quantite) return 0;
    return Math.min(100, (o.quantiteProduite / o.quantite) * 100);
  });

  dureeSansArretLabel = computed(() =>
    this.formatDuration(this.cadence()?.dureeSansArretSec ?? null),
  );

  dureeTotaleArretLabel = computed(() =>
    this.formatDuration(this.cadence()?.dureeTotaleArretSec ?? null),
  );

  /** Polyline SVG pour la sparkline (60 points). */
  sparkPath = computed(() => {
    const hist = this.cadence()?.history ?? [];
    if (hist.length < 2) return "";
    const W = 600;
    const H = 80;
    const values = hist.map((h) => h.value).filter((v) => v != null);
    if (values.length < 2) return "";
    const max = Math.max(...values);
    const min = Math.min(...values);
    const span = max - min || 1;
    const stepX = W / (values.length - 1);
    return values
      .map((v, i) => {
        const x = i * stepX;
        const y = H - ((v - min) / span) * H;
        return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ");
  });

  hasMachine = computed(() => !!this.ligne()?.deviceId);

  stateLabel = computed(() => {
    const s = this.ligne()?.state;
    switch (s) {
      case "EN_PROD": return "En production";
      case "EN_TRANSITION": return "En transition";
      case "EN_ATTENTE": return "En attente";
      case "HORS_PLAGE": return "Hors plage";
      case "DEVICE_OFFLINE": return "Hors ligne";
      default: return "Inactif";
    }
  });

  stateChipState = computed(() => {
    const s = this.ligne()?.state;
    switch (s) {
      case "EN_PROD": return "valid";
      case "EN_TRANSITION": return "warn";
      case "DEVICE_OFFLINE": return "invalid";
      case "EN_ATTENTE": return "secondary";
      default: return "basic";
    }
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: DialogData) {
    this.ligneId = data.ligneId;
  }

  formatDuration(sec: number | null): string {
    if (sec == null) return "—";
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.floor(sec % 60);
    if (h > 0) return `${h}h${String(m).padStart(2, "0")}`;
    if (m > 0) return `${m}:${String(s).padStart(2, "0")}`;
    return `${s}s`;
  }

  openOf(): void {
    const ofId = this.ligne()?.currentOf?.ofId;
    if (!ofId) return;
    window.open(`/productions/suivi/ope?ofId=${ofId}`, "_blank");
  }

  openMachineDashboard(): void {
    const reportId = this.ligne()?.reportId;
    if (!reportId) return;
    window.open(`/dashboard_prod/id/${reportId}`, "_blank");
  }

  close(): void {
    this.ref.close();
  }
}
